import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import { Building, Laptop } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Logo from '@avanta/shared/assets/avanta-logo.png';
import { AppointmentType } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { type Appointment } from '@avanta/shared/types/appointments';
import { type Patient } from '@avanta/shared/types/patients';
import { type User } from '@avanta/shared/types/users';
import { formatDateReadable, formatTime } from '@avanta/shared/utils/date';
import { DateTimeBar } from './components/DateTimeBar';
import { FieldItem } from './components/FieldItem';
import { Footer } from './components/Footer';
import { IconText } from './components/IconText';
import { SectionBox } from './components/SectionBox';
import './fonts';
import { A5_LANDSCAPE, PDF_STYLES } from './styles';
import { getCenterLines } from './utils';

export const AppointmentPDF = ({
  appointment,
  patient,
}: {
  appointment: Appointment;
  patient: Patient | User;
}): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const isRemoteAppointment = appointment.bookedContactMethod === AppointmentType.Video;
  const patientName = `${patient.firstName ?? ''} ${patient.surname ?? ''}`.trim();
  const professional = appointment.psychologist.psychologist;
  const professionalName = `${professional?.name ?? ''} ${professional?.surname ?? ''}`.trim();
  const durationInMinutes = appointment.sessionDetails?.durationInMinutes;
  const sessionType = t(
    `AppointmentPdf.sessionType.${isRemoteAppointment ? 'video' : 'in-person'}`
  );
  const sessionLabel = `${sessionType} (${durationInMinutes}min)`;
  const { name: centerName, address: centerAddress } = getCenterLines(
    appointment.center,
    isRemoteAppointment,
    t('AppointmentsReserve.FormData.remote.label')
  );
  const ageValue = patient?.age ? `${patient.age} ${t('AppointmentPdf.years.label')}` : undefined;
  const psychologistServiceBooked = appointment.psychologistServiceBooked;

  return (
    <Document>
      <Page size={A5_LANDSCAPE} style={PDF_STYLES.page}>
        <View style={PDF_STYLES.topBar} />
        <View style={PDF_STYLES.header}>
          <View style={PDF_STYLES.logoWrap}>
            <Image src={Logo} style={PDF_STYLES.logo} />
          </View>
          <View style={PDF_STYLES.headerRight}>
            <Text style={PDF_STYLES.receiptTitle}>{t('AppointmentPdf.heading')}</Text>
            <Text style={PDF_STYLES.receiptSubtitle}>{t('AppointmentPdf.subtitle')}</Text>
          </View>
        </View>
        <DateTimeBar
          dateLabel={t('AppointmentPdf.date.label')}
          dateValue={formatDateReadable(appointment.dateTime)}
          timeLabel={t('AppointmentPdf.time.label')}
          timeValue={formatTime(appointment.dateTime)}
        />
        <View style={PDF_STYLES.columnsWrap}>
          <View style={PDF_STYLES.columns}>
            <SectionBox title={t('AppointmentPdf.patient.label')}>
              <FieldItem label={t('AppointmentPdf.fullName.label')} value={patientName} />
              <View style={PDF_STYLES.fieldRow}>
                <View style={PDF_STYLES.fieldHalf}>
                  <FieldItem label={t('AppointmentPdf.nif.label')} value={patient.nif} />
                </View>
                <View style={PDF_STYLES.fieldHalf}>
                  <FieldItem label={t('AppointmentPdf.age.label')} value={ageValue} />
                </View>
              </View>
              <FieldItem label={t('AppointmentPdf.phone.label')} value={patient.contactPhone} />
            </SectionBox>
            <SectionBox title={t('AppointmentPdf.professional.label')}>
              <FieldItem label={t('AppointmentPdf.fullName.label')} value={professionalName} />
              <FieldItem
                label={t('AppointmentPdf.speciality.label')}
                value={t(`AppointmentPdf.Speciality.options.${appointment.specialitiesBookable}`)}
              />
              {psychologistServiceBooked ? (
                <FieldItem
                  label={t('AppointmentPdf.psychologistServiceBooked.label')}
                  value={t(
                    `AppointmentsReserve.Steps.treatment.options.${psychologistServiceBooked}`
                  )}
                />
              ) : null}
              <IconText icon={isRemoteAppointment ? Laptop : Building} title={sessionLabel} />
              <IconText icon={Building} title={centerName} description={centerAddress} />
            </SectionBox>
          </View>
        </View>
        <View style={PDF_STYLES.spacer} />
        <Footer
          generatedPrefix={t('AppointmentPdf.generatedPrefix')}
          generatedDate={formatDateReadable(new Date())}
          contact={t('AppointmentPdf.contact')}
          legal={t('AppointmentPdf.legal')}
        />
        <View style={PDF_STYLES.bottomBar} />
      </Page>
    </Document>
  );
};
